/**
 * @file DivisionMultiSelect.js
 * @description A multi-select dropdown component for selecting divisions.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchDivisions } from "../../../services/orgLevelService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * DivisionMultiSelect component represents a searchable multi-select dropdown for selecting divisions.
 *
 * @component
 * @param {string} className - Additional CSS class for the component.
 * @param {Array} selectedDivisions - The currently selected divisions.
 * @param {Function} onDivisionsChange - Callback function triggered when the selected divisions change.
 * @param {string} [label="Divisions"] - The label for the dropdown.
 * @param {boolean} [isRequired=false] - Indicates whether the dropdown is required.
 * @param {boolean} resetTrigger - A value that triggers the reset of the dropdown.
 * @param {string} tenantId - The ID of the tenant.
 * @param {boolean} [showSelectedItemsInField=true] - Indicates whether to show the selected items in the input field.
 * @param {boolean} [showSelectionBox=true] - Indicates whether to show the selection box.
 * @returns {JSX.Element} The rendered DivisionMultiSelect component.
 */
const DivisionMultiSelect = ({
  className,
  selectedDivisions,
  onDivisionsChange,
  label = "Divisions",
  isRequired = false,
  resetTrigger,
  tenantId,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [divisions, setDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDivisions = async () => {
      try {
        setIsLoading(true);
        const data = await fetchDivisions(tenantId);
        setDivisions(data);
      } catch (error) {
        console.error("Could not fetch divisions:", error);
        Toast.error(getText('msg_fd03cb9f9b68'));
      } finally {
        setIsLoading(false);
      }
    };

    loadDivisions();
  }, [tenantId]);

  const handleDivisionsChange = (selectedOptions) => {
    onDivisionsChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="division-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="division-dropdown"
        options={divisions}
        optionIDField="id"
        updateOptions={handleDivisionsChange}
        savedOptions={selectedDivisions}
        savedOptionIDField="id"
        disabled={divisions.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_3c91054f4689')}
      />
    </div>
  );
};

export default DivisionMultiSelect;
